import { QueryKey } from '@tanstack/react-query'
import { getSubgraphConfig, SubgraphType } from 'config/subgraph'
import type { SubgraphQueryOptions } from 'hooks/subgraph/queryOptions'
import { useConnection } from 'hooks/useConnection'
import {
  GetAllV2PositionsByOwnerQuery,
  GetV2PositionByPairQuery,
  OrderDirection,
  Pair_OrderBy,
  useGetAllV2PositionsByOwnerQuery as useGetAllV2PositionsByOwnerGraphQuery,
  useGetV2PositionByPairQuery as useGetV2PositionByPairGraphQuery,
  useV2PairByIdQuery,
  useV2PairDayDatasQuery,
  useV2PairsQuery,
  V2PairByIdQuery,
  V2PairDayDatasQuery,
  V2PairDayDatasQueryVariables,
  V2PairsQuery,
} from 'queries/v2/generated'

const getV2Endpoint = (chainId: number) => getSubgraphConfig(chainId, SubgraphType.V2).endpoint

const v2KeyBase = (chainId: number) => ['v2', chainId] as const

// Query keys
export const v2QueryKeys = {
  all: (chainId: number) => v2KeyBase(chainId),
  pairs: (chainId: number) => [...v2KeyBase(chainId), 'pairs'] as const,
  pair: (chainId: number, id: string) => [...v2KeyBase(chainId), 'pair', id] as const,
  pairDayDatas: (chainId: number, pairAddress: string, skip: number) =>
    [...v2KeyBase(chainId), 'pairDayDatas', pairAddress, skip] as const,
  pairsList: (chainId: number, skip: number, first: number, orderBy: Pair_OrderBy, orderDirection: OrderDirection) =>
    [...v2QueryKeys.pairs(chainId), skip, first, orderBy, orderDirection] as const,
  userPositions: (chainId: number, user: string, includeZeroBalance: boolean) =>
    [...v2KeyBase(chainId), 'userPositions', user, includeZeroBalance ? 'all' : 'positive'] as const,
  userPositionByPair: (chainId: number, user: string, pairAddress: string, includeZeroBalance: boolean) =>
    [...v2KeyBase(chainId), 'userPositionByPair', user, pairAddress, includeZeroBalance ? 'all' : 'positive'] as const,
}

// Types
type PairData = V2PairByIdQuery
type PairDayDatasResponse = V2PairDayDatasQuery
type PairsListResponse = V2PairsQuery
type UserV2PositionsResponse = GetAllV2PositionsByOwnerQuery
type V2PositionByPairResponse = GetV2PositionByPairQuery

export type PairDayDatasVariables = Omit<V2PairDayDatasQueryVariables, 'skip'> & { skip?: number }
export type PairsListParams = {
  skip?: number
  first?: number
  orderBy?: Pair_OrderBy
  orderDirection?: OrderDirection
}

export const usePairById = (id: string, options?: SubgraphQueryOptions<PairData>) => {
  const { chainId } = useConnection()
  const normalizedId = id.toLowerCase()
  const { skip, enabled: optionEnabled, ...queryOptions } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skip && Boolean(normalizedId)
  const endpoint = getV2Endpoint(chainId)

  return useV2PairByIdQuery(
    { endpoint },
    { id: normalizedId },
    {
      ...queryOptions,
      enabled,
      queryKey: (options?.queryKey ?? v2QueryKeys.pair(chainId, normalizedId)) as QueryKey,
    }
  )
}

export const useGetAllV2PositionsByOwner = (
  variables: { user: string; includeZeroBalance?: boolean },
  options?: SubgraphQueryOptions<UserV2PositionsResponse, GetAllV2PositionsByOwnerQuery>
) => {
  const { chainId } = useConnection()
  const user = variables.user.toLowerCase()
  const includeZeroBalance = variables.includeZeroBalance ?? false
  const minBalance = includeZeroBalance ? '-1' : '0'
  const { skip, enabled: optionEnabled, ...queryOptions } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skip && Boolean(user)
  const endpoint = getV2Endpoint(chainId)

  return useGetAllV2PositionsByOwnerGraphQuery<UserV2PositionsResponse>(
    { endpoint },
    { user, minBalance },
    {
      ...queryOptions,
      enabled,
      queryKey: (options?.queryKey ?? v2QueryKeys.userPositions(chainId, user, includeZeroBalance)) as QueryKey,
    }
  )
}

export const useV2PositionByPair = (
  variables: { user: string; pairAddress: string; includeZeroBalance?: boolean },
  options?: SubgraphQueryOptions<V2PositionByPairResponse, GetV2PositionByPairQuery>
) => {
  const { chainId } = useConnection()
  const user = variables.user.toLowerCase()
  const pairAddress = variables.pairAddress.toLowerCase()
  const includeZeroBalance = variables.includeZeroBalance ?? false
  const minBalance = includeZeroBalance ? '-1' : '0'
  const { skip, enabled: optionEnabled, ...queryOptions } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skip && Boolean(user) && Boolean(pairAddress)
  const endpoint = getV2Endpoint(chainId)

  return useGetV2PositionByPairGraphQuery<V2PositionByPairResponse>(
    { endpoint },
    { user, pairId: pairAddress, minBalance },
    {
      ...queryOptions,
      enabled,
      queryKey: (options?.queryKey ??
        v2QueryKeys.userPositionByPair(chainId, user, pairAddress, includeZeroBalance)) as QueryKey,
    }
  )
}

export const usePairDayDatas = (
  variables: PairDayDatasVariables,
  options?: SubgraphQueryOptions<PairDayDatasResponse>
) => {
  const { chainId } = useConnection()
  const pairAddress = variables.pairAddress.toLowerCase()
  const skipValue = variables.skip ?? 0
  const { skip, enabled: optionEnabled, ...queryOptions } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skip && Boolean(pairAddress)
  const endpoint = getV2Endpoint(chainId)

  return useV2PairDayDatasQuery(
    { endpoint },
    { pairAddress, skip: skipValue },
    {
      ...queryOptions,
      enabled,
      queryKey: (options?.queryKey ?? v2QueryKeys.pairDayDatas(chainId, pairAddress, skipValue)) as QueryKey,
    }
  )
}

export const usePairs = (params: PairsListParams = {}, options?: SubgraphQueryOptions<PairsListResponse>) => {
  const { chainId } = useConnection()
  const { skip = 0, first = 100, orderBy = Pair_OrderBy.ReserveUsd, orderDirection = OrderDirection.Desc } = params
  const { skip: skipOption, enabled: optionEnabled, ...queryOptions } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skipOption
  const endpoint = getV2Endpoint(chainId)

  return useV2PairsQuery(
    { endpoint },
    { skip, first, orderBy, orderDirection },
    {
      ...queryOptions,
      enabled,
      queryKey: (options?.queryKey ?? v2QueryKeys.pairsList(chainId, skip, first, orderBy, orderDirection)) as QueryKey,
    }
  )
}
